import { EngineFailure, toEngineError } from '../../core/errors';
import type { ConversionProgress, ConversionResult, ConverterCapabilities, EngineError } from '../../core/types';
import {
  WORKER_PROTOCOL_VERSION,
  isWorkerResponse,
  type ConvertRequestPayload,
  type WorkerRequest,
} from '../../core/worker-protocol';

/** 只依赖最小接口，便于测试注入 loopback 实现。 */
export interface WorkerLike {
  postMessage(message: unknown, transfer?: Transferable[]): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  terminate(): void;
}

export interface RunOptions {
  onProgress?: (progress: ConversionProgress) => void;
  signal?: AbortSignal;
}

export interface ConversionDriver {
  run(payload: ConvertRequestPayload, options?: RunOptions): Promise<ConversionResult>;
  cancel(id: string): void;
  /** 向 Worker 查询运行时能力（AVIF WASM 等），超时按失败处理。 */
  requestCapabilities(): Promise<ConverterCapabilities[]>;
  /** 排队中 + 执行中的任务数。 */
  pendingCount(): number;
  dispose(): void;
}

interface QueuedTask {
  id: string;
  payload: ConvertRequestPayload;
  options: RunOptions;
  resolve: (result: ConversionResult) => void;
  reject: (error: unknown) => void;
  state: 'queued' | 'running' | 'finished';
  detachSignal?: () => void;
  cancelTimer?: ReturnType<typeof setTimeout>;
}

export interface DriverOptions {
  /** 取消后等待 Worker 响应的宽限时间，超时则重建 Worker，避免卡死。 */
  cancelGraceMs?: number;
  /** 能力查询超时时间。 */
  capabilityTimeoutMs?: number;
  createTaskId?: () => string;
}

interface CapabilityRequest {
  resolve: (capabilities: ConverterCapabilities[]) => void;
  reject: (error: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

let taskCounter = 0;

function defaultTaskId(): string {
  taskCounter += 1;
  return `t_${Date.now().toString(36)}_${taskCounter}`;
}

/**
 * 主线程驱动：单 Worker + 串行队列。
 * 同一时刻只向 Worker 派发一个任务，避免大文件并发解码把内存打满。
 */
export function createConversionDriver(factory: () => WorkerLike, options: DriverOptions = {}): ConversionDriver {
  const cancelGraceMs = options.cancelGraceMs ?? 5000;
  const capabilityTimeoutMs = options.capabilityTimeoutMs ?? 15000;
  const createTaskId = options.createTaskId ?? defaultTaskId;

  const queue: QueuedTask[] = [];
  const inflight = new Map<string, QueuedTask>();
  const capabilityPending = new Map<string, CapabilityRequest>();
  let worker: WorkerLike | null = null;
  let disposed = false;

  function handleMessage(event: { data: unknown }): void {
    const message = event.data;
    if (!isWorkerResponse(message)) return;

    if (message.type === 'capabilities') {
      const pending = capabilityPending.get(message.id);
      if (!pending) return;
      clearTimeout(pending.timer);
      capabilityPending.delete(message.id);
      pending.resolve(message.capabilities);
      return;
    }

    const task = inflight.get(message.id);
    if (!task) return;

    if (message.type === 'progress') {
      task.options.onProgress?.(message.progress);
      return;
    }
    if (message.ok && message.result) {
      settle(task, message.result);
      return;
    }
    settleFailure(task, message.error ?? { code: 'INTERNAL_ERROR', message: '转换失败' });
  }

  function ensureWorker(): WorkerLike {
    if (worker) return worker;
    const next = factory();
    next.addEventListener('message', handleMessage);
    worker = next;
    return next;
  }

  function resetWorker(): void {
    if (!worker) return;
    worker.removeEventListener('message', handleMessage);
    worker.terminate();
    worker = null;
  }

  function finish(task: QueuedTask): void {
    task.state = 'finished';
    if (task.cancelTimer) clearTimeout(task.cancelTimer);
    task.detachSignal?.();
    inflight.delete(task.id);
    pump();
  }

  function settle(task: QueuedTask, result: ConversionResult): void {
    if (task.state === 'finished') return;
    finish(task);
    task.resolve(result);
  }

  function settleFailure(task: QueuedTask, error: EngineError): void {
    if (task.state === 'finished') return;
    finish(task);
    task.reject(new EngineFailure(error.code, error.message));
  }

  function pump(): void {
    if (disposed || inflight.size > 0) return;
    const next = queue.shift();
    if (!next) return;
    next.state = 'running';
    inflight.set(next.id, next);
    const request: WorkerRequest = {
      id: next.id,
      version: WORKER_PROTOCOL_VERSION,
      type: 'convert',
      payload: next.payload,
    };
    try {
      ensureWorker().postMessage(request, [next.payload.buffer]);
    } catch (error) {
      settleFailure(next, toEngineError(error));
    }
  }

  function cancelTask(id: string): void {
    const queuedIndex = queue.findIndex((task) => task.id === id);
    if (queuedIndex >= 0) {
      const [task] = queue.splice(queuedIndex, 1);
      task.state = 'finished';
      task.detachSignal?.();
      task.reject(new EngineFailure('CANCELLED', '任务已取消'));
      return;
    }

    const running = inflight.get(id);
    if (!running || running.state !== 'running') return;

    const request: WorkerRequest = { id, version: WORKER_PROTOCOL_VERSION, type: 'cancel' };
    worker?.postMessage(request);
    if (!running.cancelTimer) {
      running.cancelTimer = setTimeout(() => {
        // 引擎未响应取消：重建 Worker，避免被卡住的线程拖住后续任务。
        resetWorker();
        settleFailure(running, { code: 'CANCELLED', message: '任务已取消' });
      }, cancelGraceMs);
    }
  }

  function runTask(payload: ConvertRequestPayload, runOptions: RunOptions = {}): Promise<ConversionResult> {
    if (disposed) return Promise.reject(new EngineFailure('INTERNAL_ERROR', '转换驱动已释放'));
    const signal = runOptions.signal;
    if (signal?.aborted) return Promise.reject(new EngineFailure('CANCELLED', '任务已取消'));

    const id = createTaskId();
    return new Promise<ConversionResult>((resolve, reject) => {
      const task: QueuedTask = { id, payload, options: runOptions, resolve, reject, state: 'queued' };

      if (signal) {
        const onAbort = () => cancelTask(id);
        signal.addEventListener('abort', onAbort, { once: true });
        task.detachSignal = () => signal.removeEventListener('abort', onAbort);
      }

      queue.push(task);
      pump();
    });
  }

  function pendingCount(): number {
    return queue.length + inflight.size;
  }

  function requestCapabilities(): Promise<ConverterCapabilities[]> {
    if (disposed) return Promise.reject(new EngineFailure('INTERNAL_ERROR', '转换驱动已释放'));
    const id = createTaskId();
    return new Promise<ConverterCapabilities[]>((resolve, reject) => {
      const timer = setTimeout(() => {
        capabilityPending.delete(id);
        reject(new EngineFailure('INTERNAL_ERROR', '转换能力查询超时'));
      }, capabilityTimeoutMs);
      capabilityPending.set(id, { resolve, reject, timer });
      const request: WorkerRequest = { id, version: WORKER_PROTOCOL_VERSION, type: 'capabilities' };
      try {
        ensureWorker().postMessage(request);
      } catch (error) {
        clearTimeout(timer);
        capabilityPending.delete(id);
        reject(new EngineFailure('INTERNAL_ERROR', toEngineError(error).message));
      }
    });
  }

  function dispose(): void {
    disposed = true;
    resetWorker();
    const pending = [...queue, ...inflight.values()];
    queue.length = 0;
    inflight.clear();
    for (const task of pending) {
      if (task.state === 'finished') continue;
      task.state = 'finished';
      task.detachSignal?.();
      if (task.cancelTimer) clearTimeout(task.cancelTimer);
      task.reject(new EngineFailure('CANCELLED', '转换驱动已释放'));
    }
    for (const [id, capability] of capabilityPending) {
      clearTimeout(capability.timer);
      capabilityPending.delete(id);
      capability.reject(new EngineFailure('CANCELLED', '转换驱动已释放'));
    }
  }

  return { run: runTask, cancel: cancelTask, requestCapabilities, pendingCount, dispose };
}

/** 浏览器实现：Vite 负责把 Worker 打包成 ESM。 */
export function createBrowserWorker(): WorkerLike {
  return new Worker(new URL('./conversion.worker.ts', import.meta.url), { type: 'module' }) as unknown as WorkerLike;
}
